import React, {useMemo, useState} from 'react';
import {
  DashboardLayout,
  Filter,
  PrimaryButton,
  SecondaryButton,
  SkeletonLoader,
} from '../../components';
import {
  abandoned,
  failed,
  inProgress,
  successful,
} from '../../assets/images/images';
import BarChart from './AuthenticationBarChart';
import {useAllApps} from '../../hooks/useApps';
import {useQuery} from 'react-query';
import {fetchAuthenticationCustomersAnalytics} from '../../requests/queries/easyAuth';
import {useNavigate, useParams} from 'react-router-dom';
import {shortenLargeNumbers} from '../../helpers/shortenLargeNumbers';
import moment from 'moment';
import FilterV2 from 'components/FilterV2';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';

function AuthenticationAnalytics() {
  const {appIds} = useAllApps();
  const appIDS = appIds?.join(',');
  const {slug} = useParams();
  const [openFilter, setOpenFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const navigate = useNavigate();

  const handleFilterChange = newValues => {
    setFilterValues(newValues);
  };

  const dateParams = filterValues.dateRange
    ? {
        start_date: moment(filterValues.dateRange.start).format('YYYY-MM-DD'),
        end_date: moment(filterValues.dateRange.end).format('YYYY-MM-DD'),
      }
    : {};

  const {data: authCustomersAnalytics, isLoading} = useQuery(
    ['auth-customers', {app_ids: appIDS, reference_id: slug, ...dateParams}],
    () =>
      fetchAuthenticationCustomersAnalytics({
        app_ids: appIDS,
        reference_id: slug,
        ...dateParams,
      }),
    {
      enabled: appIds?.length > 0 && slug?.length > 0,
      retry: 1,
    },
  );

  const analysisData = useMemo(
    () => !isLoading && authCustomersAnalytics?.data,
    [authCustomersAnalytics?.data, isLoading],
  );

  const analyticCards = useMemo(
    () => [
      {
        title: 'Successful',
        value: analysisData?.cards?.Completed,
        icon: successful,
      },
      {
        title: 'Failed',
        value: analysisData?.cards?.Failed,
        icon: failed,
      },
      {
        title: 'Abandoned',
        value: analysisData?.cards?.Abandoned,
        icon: abandoned,
      },
      {
        title: 'Authentications',
        value: analysisData?.cards?.Total,
        icon: inProgress,
      },
    ],
    [
      analysisData?.cards?.Abandoned,
      analysisData?.cards?.Completed,
      analysisData?.cards?.Failed,
      analysisData?.cards?.Total,
    ],
  );

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          Authentication Analysis
        </p>
      }
    >
      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={() => {}}
        filterOptions={[]}
      />

      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <Breadcrumbs
          items={[
            {
              label: 'Authentication Customers',
              path: PAGES_URL.AUTHENTICATION_CUSTOMERS,
            },
            {label: 'Authentication Analysis'},
          ]}
        />
      </div>

      <div className="flex flex-col border border-transparent border-b-[#E4E4E6] py-2 lg:flex-row gap-6 mr-[-24px] ml-[-32px] px-8 lg:gap-0 lg:items-center justify-between">
        <div className="flex flex-col gap-1 items-start">
          <div className="flex items-center">
            <p className="text-grey-200">Ref: </p>
            <h1 className="font-medium text-grey text-[16px] ml-2 mb-">
              {slug || '-'}
            </h1>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
          <PrimaryButton
            buttonText="View Authentications"
            onClick={() =>
              navigate('/authentications', {
                state: {
                  refId: slug,
                },
              })
            }
          />
        </div>
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 onFilterChange={handleFilterChange} filters={[]} />
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {analyticCards?.map(data => (
          <div
            key={data?.title}
            className="bg-white border py-4 px-6 border-[#E4E7EC] shadow-cards relative flex-wrap rounded-xl flex items-center gap-2 lg:flex-col lg:items-start xl:flex-row xl:items-center"
          >
            <div className="flex flex-col">
              <p className="text-sm text-[#667185] font-medium">
                {data?.title}
              </p>
              <p className="font-semibold text-[32px] tracking-[-0.02em] text-[#1D2739]">
                {isLoading ? (
                  <div className="mt-2">
                    <SkeletonLoader
                      style={{width: '100%', height: 20, maxWidth: 100}}
                    />
                  </div>
                ) : (
                  shortenLargeNumbers(data?.value || 0, null, {
                    decimals: 0,
                    startAt: 'B',
                  })
                )}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white shadow-cards rounded-xl border border-[#EFEFEF]">
        <div className="px-6 py-3 bg-[#F9F9F9] rounded-t-xl relative flex items-center justify-between w-full">
          <p className="text-base text-[#4A4A68] font-medium">
            No. of Authentications
          </p>
        </div>
        <BarChart
          isLoading={isLoading}
          analysisData={analysisData?.monthly_chart}
          dateRange={filterValues.dateRange}
        />
      </div>
    </DashboardLayout>
  );
}

export default AuthenticationAnalytics;
